/**
 * Alerts & incidents.
 *
 * This view used to render `Rules loaded: 9` as a string literal, with a "live" badge, while
 * 23 rules were running. It was wrong within a day of being written and had no way of knowing.
 *
 * Everything here now comes from Prometheus through the operations API.
 *
 * The distinction this file is built around: **nothing firing** and **we cannot tell** are
 * different facts that a careless dashboard renders identically. A green "0 firing" during a
 * Prometheus outage is worse than an error, because it actively tells an operator to relax.
 * When the API returns 503 this view says so and shows no counts at all.
 */

import { useEffect, useState } from 'react';

import { Reading } from '../Reading';
import { getAlerts, type AlertsSnapshot, type ApiFailure, type FiringAlert } from '../api';
import { centralClock } from '../time';

export function Alerts() {
  const [snap, setSnap] = useState<AlertsSnapshot | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function tick() {
      const r = await getAlerts();
      if (!alive) return;
      setLoading(false);
      // Keep the last snapshot so a single failed poll shows as stale rather than blank.
      if (r.ok) { setSnap(r.data); setFailure(null); } else { setFailure(r.failure); }
    }
    void tick();
    const t = setInterval(() => void tick(), 15_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading && !snap) {
    return (
      <div className="readings">
        <Reading label="Alert rules" state="unknown" source="loading" reason="Reading from Prometheus." />
      </div>
    );
  }

  // No snapshot at all: show the failure and NOTHING else. Any number here would be invented.
  if (!snap) {
    return (
      <div className="notice red">
        <h3>Alert state cannot be read</h3>
        <p>{failure && 'reason' in failure ? failure.reason : 'The Operations API did not respond.'}</p>
        <p>
          This is not the same as nothing being wrong. Alerts may be firing right now with
          nobody able to see them, so treat every other view as unverified until this clears.
        </p>
      </div>
    );
  }

  const stale = failure !== null;
  const { rules, firing, watchdogFiring, incidents } = snap;
  const critical = firing.filter((f) => f.severity === 'critical').length;

  return (
    <>
      {failure && (
        <div className="notice red">
          <h3>These numbers are no longer current</h3>
          <p>
            {'reason' in failure ? failure.reason : 'The last refresh failed.'} What you see
            below is the last successful reading, not the state right now.
          </p>
        </div>
      )}

      {!watchdogFiring && (
        <div className="notice red">
          <h3>The watchdog is not firing</h3>
          <p>
            One rule is designed to fire constantly. Its silence means alert rules are not
            evaluating, so an absence of alerts below proves nothing. Nothing on this page can
            be trusted until it returns.
          </p>
        </div>
      )}

      <div className="readings">
        <Reading
          label="Rules loaded" value={String(rules.loaded)}
          state={stale ? 'stale' : 'live'} source="Prometheus"
          observedAt={new Date(snap.observedAt)} environment="production"
          reason={`${rules.provisional} carry provisional thresholds`}
        />
        <Reading
          label="Firing now" value={String(firing.length)}
          state={stale ? 'stale' : 'live'} source="Prometheus"
          observedAt={new Date(snap.observedAt)} environment="production"
          reason={critical > 0 ? `${critical} critical` : 'the watchdog is excluded; it always fires'}
        />
        <Reading
          label="Open incidents" value={String(incidents.open)}
          state={stale ? 'stale' : 'live'} source="operations database"
          observedAt={new Date(snap.observedAt)} environment="production"
          reason={`${incidents.acknowledged} acknowledged`}
        />
        {rules.unhealthy > 0 ? (
          <Reading
            label="Rules not evaluating" value={String(rules.unhealthy)}
            state="error" source="Prometheus"
            reason={rules.unhealthyNames.join(', ')}
          />
        ) : (
          <Reading
            label="Rules not evaluating" value="0"
            state={stale ? 'stale' : 'live'} source="Prometheus"
            observedAt={new Date(snap.observedAt)} environment="production"
            reason="every rule is evaluating without error"
          />
        )}
      </div>

      {firing.length > 0 ? (
        <div className="alert-list">
          {firing.map((f) => <AlertRow key={`${f.alertname}:${f.service ?? ''}`} alert={f} />)}
        </div>
      ) : (
        <div className="notice">
          <h3>Nothing is firing</h3>
          <p>
            Verified against Prometheus at {centralClock(snap.observedAt)}, with
            the watchdog confirming rules are evaluating. This is a measured result, not an
            empty list.
          </p>
        </div>
      )}

      <div className="notice blue">
        <h3>Acknowledging is not resolving</h3>
        <p>
          Acknowledging an incident records that a person saw it. It does not close the
          incident, silence the rule, or stop the alert firing again.
        </p>
      </div>

      {rules.provisional > 0 && (
        <div className="notice">
          <h3>{rules.provisional} of {rules.loaded} thresholds are still candidates</h3>
          <p>
            Those numbers were chosen to be quiet, not measured from a baseline. Retention was
            raised to 30 days on 23 September, so tuning them against real behaviour becomes
            possible from late October.
          </p>
        </div>
      )}
    </>
  );
}

function AlertRow({ alert }: { alert: FiringAlert }) {
  const since = alert.activeSince ? new Date(alert.activeSince) : null;
  return (
    <div className={`alert-row sev-${alert.severity}`}>
      <div className="alert-head">
        <span className={`pill sev-${alert.severity}`}>{alert.severity}</span>
        <strong>{alert.alertname}</strong>
        {alert.provisional && <span className="pill muted" title="Threshold not tuned from a baseline">provisional</span>}
      </div>
      <p className="alert-summary">{alert.summary ?? 'No summary on this rule.'}</p>
      <div className="alert-meta">
        {alert.service && <span>{alert.service}</span>}
        {alert.environment && <span>{alert.environment}</span>}
        {since && <span>firing for {elapsed(since)}</span>}
        {alert.runbookUrl && <a href={alert.runbookUrl} target="_blank" rel="noreferrer">runbook</a>}
      </div>
    </div>
  );
}

function elapsed(since: Date): string {
  const mins = Math.max(0, Math.round((Date.now() - since.getTime()) / 60_000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
}
